/* Interactive chart wrapper.
 *
 * Driven by a JSON spec the `interactive-chart` skill emits inside a
 * fenced ```chart … ``` block. MarkdownView detects that fence and
 * mounts this component. Single source of truth for chat AND agent
 * run results — both surfaces route skill output through MarkdownView.
 *
 * Supported `type` values (matches the MUI X Charts catalog the user
 * referenced, themed with the Eraneos palette):
 *
 *   bar, line, area, pie, donut, scatter, radar, composed   (Recharts)
 *   sparkline, gauge, funnel, sankey                        (Recharts)
 *   heatmap, candlestick                                    (custom SVG)
 *
 * Eraneos palette: first series in `#FF6428` (focal); the rest cycle
 * through the supporting palette. Caller can override per-key in
 * `spec.colors`.
 */
import React from 'react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, ComposedChart, ScatterChart, Scatter,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  RadialBarChart, RadialBar,
  FunnelChart, Funnel, LabelList,
  Sankey,
} from 'recharts';

const PALETTE = [
  '#FF6428', '#715AAE', '#A20235', '#00AF37',
  '#BBD2FF', '#FFC3B1', '#471D65', '#DE3015', '#BFA6E6',
];

const INK   = '#202020';
const MUTED = '#93867D';
const RULE  = '#E5DDD5';

const UP_GREEN  = '#00AF37';
const DOWN_RED  = '#DE3015';

function pickColor(key, colors, idx) {
  return (colors && colors[key]) || PALETTE[idx % PALETTE.length];
}

const tooltipChrome = {
  contentStyle: {
    background: '#fff',
    border: `1px solid ${RULE}`,
    borderRadius: 8,
    fontSize: 12,
    boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
  },
  labelStyle: { color: INK, fontWeight: 600 },
  itemStyle: { color: INK },
};

const axisProps = {
  stroke: MUTED,
  tick: { fill: MUTED, fontSize: 11 },
  axisLine: { stroke: RULE },
  tickLine: { stroke: RULE },
};

export const InteractiveChart = React.memo(function InteractiveChart({ spec }) {
  const cardRef = React.useRef(null);
  if (!spec || typeof spec !== 'object') {
    return <ErrChart msg="Empty chart spec."/>;
  }
  const type = String(spec.type || 'bar').toLowerCase();
  const colors  = spec.colors || {};
  const xKey    = spec.xKey || 'name';
  const height  = Math.max(120, Math.min(560, Number(spec.height) || 320));
  const title   = spec.title;
  const stacked = !!spec.stacked;

  // Sparkline is a tiny chrome-less variant — render without the card
  // shell so it can sit inline (in a table cell, KPI strip, etc.).
  if (type === 'sparkline') {
    return <Sparkline spec={spec}/>;
  }

  const fileBase = (title || `chart-${type}`).toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 60) || 'chart';

  return (
    <div ref={cardRef} style={{margin:'4px 0 12px',background:'transparent',padding:'4px 0',position:'relative'}}>
      <div className="ic-export" style={{position:'absolute',top:8,right:8,display:'flex',gap:6,opacity:0.6}}>
        <button
          type="button"
          onClick={() => exportChartImage(cardRef.current, fileBase, 'png')}
          title="Download as PNG"
          style={chartExportBtnStyle}
        >
          PNG
        </button>
        <button
          type="button"
          onClick={() => exportChartImage(cardRef.current, fileBase, 'jpg')}
          title="Download as JPG"
          style={chartExportBtnStyle}
        >
          JPG
        </button>
      </div>
      {title && (
        <div style={{fontSize:13,fontWeight:600,color:INK,marginBottom:6,paddingRight:90}}>
          {title}
        </div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        {renderInner(type, spec, { colors, xKey, stacked }) || <div/>}
      </ResponsiveContainer>
      {spec.footnote && (
        <div style={{fontSize:11,color:MUTED,marginTop:6}}>{spec.footnote}</div>
      )}
    </div>
  );
});

const chartExportBtnStyle = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.04em',
  padding: '2px 7px',
  borderRadius: 4,
  background: '#fff',
  border: `1px solid ${RULE}`,
  color: MUTED,
  cursor: 'pointer',
};

/* Pure-client export: serialise the chart's SVG (Recharts produces
 * inline SVG) to a string, draw it onto a canvas at 2× DPR so the
 * output is sharp, then download as PNG or JPG. Title + footnote
 * are painted onto the canvas separately so they survive the export
 * (they're regular HTML in the live view, not part of the SVG).
 * No new dependency, ~50 lines of vanilla DOM. */
async function exportChartImage(cardEl, fileBase, format) {
  if (!cardEl) return;
  const svg = cardEl.querySelector('svg');
  if (!svg) { alert('Could not find chart SVG to export.'); return; }

  const DPR = Math.min(3, (window.devicePixelRatio || 1) * 2);
  const rect = svg.getBoundingClientRect();
  const w = Math.round(rect.width);
  const h = Math.round(rect.height);

  // Inline computed font + colour on the SVG so the serialised
  // string carries the same look the user saw.
  const clone = svg.cloneNode(true);
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  if (!clone.getAttribute('width'))  clone.setAttribute('width', String(w));
  if (!clone.getAttribute('height')) clone.setAttribute('height', String(h));
  // Apply a default font-family so text doesn't fall back to Times.
  clone.style.fontFamily = 'Mona Sans, system-ui, -apple-system, sans-serif';

  // Headline / footnote text from the card itself, painted onto the
  // canvas after the chart. Read directly so we capture overrides.
  const titleEl = cardEl.querySelector('div > div:first-child');
  const titleText = titleEl && titleEl.textContent !== '' && !titleEl.querySelector('svg') ? titleEl.textContent.trim() : '';
  const footEl = cardEl.querySelector('div > div:last-child');
  const footText = footEl && footEl !== titleEl && footEl.textContent !== '' && !footEl.querySelector('svg') && !footEl.querySelector('button')
    ? footEl.textContent.trim() : '';

  const svgString = new XMLSerializer().serializeToString(clone);
  const svgUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);

  const titleHeight = titleText ? 28 : 0;
  const footHeight  = footText ? 22 : 0;
  const padding     = 14;
  const canvasW = (w + padding * 2) * DPR;
  const canvasH = (h + titleHeight + footHeight + padding * 2) * DPR;

  const canvas = document.createElement('canvas');
  canvas.width  = canvasW;
  canvas.height = canvasH;
  const ctx = canvas.getContext('2d');
  ctx.scale(DPR, DPR);

  // Background — white. JPG can't be transparent, so we paint a solid
  // white backdrop regardless of format.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvasW / DPR, canvasH / DPR);

  // Title.
  if (titleText) {
    ctx.fillStyle = '#202020';
    ctx.font = '600 14px "Mona Sans", system-ui, sans-serif';
    ctx.textBaseline = 'top';
    ctx.fillText(titleText, padding, padding);
  }

  // Wait for the SVG to load as an Image, then draw to canvas.
  await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      ctx.drawImage(img, padding, padding + titleHeight, w, h);
      resolve(undefined);
    };
    img.onerror = reject;
    img.src = svgUrl;
  }).catch(err => {
    alert(`Export failed: ${err?.message || 'could not load SVG'}`);
    throw err;
  });

  // Footnote.
  if (footText) {
    ctx.fillStyle = '#93867D';
    ctx.font = '11px "Mona Sans", system-ui, sans-serif';
    ctx.textBaseline = 'top';
    ctx.fillText(footText, padding, padding + titleHeight + h + 4);
  }

  // Trigger download.
  const mime = format === 'jpg' ? 'image/jpeg' : 'image/png';
  const ext  = format === 'jpg' ? 'jpg' : 'png';
  const dataUrl = canvas.toDataURL(mime, format === 'jpg' ? 0.92 : 1.0);
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = `${fileBase}.${ext}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function renderInner(type, spec, ctx) {
  const { colors, xKey, stacked } = ctx;
  const data = Array.isArray(spec.data) ? spec.data : [];

  /* ---------- custom SVG renderers (no Recharts equivalent) ---------- */

  if (type === 'heatmap') {
    return <HeatmapBody spec={spec}/>;
  }
  if (type === 'candlestick') {
    return <CandlestickBody spec={spec}/>;
  }
  if (type === 'gauge') {
    // Recharts RadialBarChart handles this nicely. Single-value gauge
    // with target / max framing.
    const value = Number(spec.value ?? data[0]?.value ?? 0);
    const max   = Number(spec.max   ?? 100);
    const pct   = Math.max(0, Math.min(1, value / (max || 1)));
    const arc   = [{ name: 'value', v: pct * 100, fill: pickColor('value', colors, 0) }];
    return (
      <RadialBarChart
        innerRadius="65%" outerRadius="100%" startAngle={210} endAngle={-30}
        data={arc} barSize={18}
      >
        <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false}/>
        <RadialBar background={{ fill: '#F1E9E1' }} dataKey="v" cornerRadius={9}/>
        <text x="50%" y="48%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 22, fontWeight: 700, fill: INK }}>
          {spec.format === 'percent' ? `${Math.round(pct * 100)}%` : value}
        </text>
        {spec.label && (
          <text x="50%" y="64%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 11, fill: MUTED }}>
            {spec.label}
          </text>
        )}
      </RadialBarChart>
    );
  }

  if (type === 'funnel') {
    // Funnel rows: [{ name, value }, …] in descending order.
    const rows = data.length ? data : [];
    const coloured = rows.map((r, i) => ({ ...r, fill: pickColor(String(i), colors, i) }));
    return (
      <FunnelChart>
        <Tooltip {...tooltipChrome}/>
        <Funnel data={coloured} dataKey={spec.valueKey || 'value'} nameKey={spec.nameKey || xKey} isAnimationActive>
          <LabelList position="right" fill={INK} stroke="none" dataKey={spec.nameKey || xKey} style={{ fontSize: 11 }}/>
        </Funnel>
      </FunnelChart>
    );
  }

  if (type === 'sankey') {
    // Sankey spec: { nodes: [{name}], links: [{source, target, value}] }
    const nodes = Array.isArray(spec.nodes) ? spec.nodes : [];
    const links = Array.isArray(spec.links) ? spec.links : [];
    if (!nodes.length || !links.length) {
      return <ErrInline msg="Sankey needs nodes[] + links[]."/>;
    }
    return (
      <Sankey
        data={{ nodes, links }}
        nodePadding={24} nodeWidth={10} margin={{ top: 12, bottom: 12, left: 12, right: 12 }}
        link={{ stroke: PALETTE[0], strokeOpacity: 0.25 }}
        node={{ fill: PALETTE[1], stroke: PALETTE[1] }}
      >
        <Tooltip {...tooltipChrome}/>
      </Sankey>
    );
  }

  /* ---------- Recharts series-shaped charts ---------- */

  if (data.length === 0) return <ErrInline msg="No data points."/>;

  // Series-key resolution. spec.series = ['revenue', 'ebitda'] OR
  // derived from object keys (everything except the x-key).
  const seriesKeys = Array.isArray(spec.series) && spec.series.length
    ? spec.series.map(s => typeof s === 'string' ? { key: s, name: s, kind: null } : { key: s.key, name: s.name || s.key, kind: s.kind || null })
    : Object.keys(data[0] || {}).filter(k => k !== xKey).map(k => ({ key: k, name: k, kind: null }));

  if (type === 'pie' || type === 'donut') {
    const valueKey = (spec.valueKey || seriesKeys[0]?.key) || 'value';
    const innerRadius = type === 'donut' ? '55%' : 0;
    return (
      <PieChart>
        <Pie
          data={data}
          dataKey={valueKey}
          nameKey={xKey}
          innerRadius={innerRadius}
          outerRadius="80%"
          paddingAngle={1}
          stroke="#fff"
        >
          {data.map((_, i) => <Cell key={i} fill={pickColor(String(i), colors, i)}/>)}
        </Pie>
        <Tooltip {...tooltipChrome}/>
        <Legend wrapperStyle={{ fontSize: 12, color: INK }}/>
      </PieChart>
    );
  }

  if (type === 'radar') {
    return (
      <RadarChart data={data}>
        <PolarGrid stroke={RULE}/>
        <PolarAngleAxis dataKey={xKey} stroke={MUTED} tick={{ fill: MUTED, fontSize: 11 }}/>
        <PolarRadiusAxis stroke={MUTED}/>
        {seriesKeys.map((s, i) => (
          <Radar key={s.key} dataKey={s.key} name={s.name} stroke={pickColor(s.key, colors, i)} fill={pickColor(s.key, colors, i)} fillOpacity={0.25}/>
        ))}
        <Tooltip {...tooltipChrome}/>
        <Legend wrapperStyle={{ fontSize: 12, color: INK }}/>
      </RadarChart>
    );
  }

  if (type === 'scatter') {
    return (
      <ScatterChart>
        <CartesianGrid stroke={RULE} strokeDasharray="3 3"/>
        <XAxis dataKey={xKey} {...axisProps}/>
        <YAxis {...axisProps}/>
        <Tooltip {...tooltipChrome}/>
        <Legend wrapperStyle={{ fontSize: 12, color: INK }}/>
        {seriesKeys.map((s, i) => (
          <Scatter key={s.key} name={s.name} data={data} dataKey={s.key} fill={pickColor(s.key, colors, i)}/>
        ))}
      </ScatterChart>
    );
  }

  if (type === 'composed') {
    // Mixed bars + lines + areas on the same chart — exactly the
    // "revenue (bars) + EBITDA (line)" case from the user's last chat.
    const bars  = (spec.bars  || []).map(String);
    const lines = (spec.lines || []).map(String);
    const areas = (spec.areas || []).map(String);
    return (
      <ComposedChart data={data}>
        <CartesianGrid stroke={RULE} strokeDasharray="3 3"/>
        <XAxis dataKey={xKey} {...axisProps}/>
        <YAxis {...axisProps}/>
        <Tooltip {...tooltipChrome}/>
        <Legend wrapperStyle={{ fontSize: 12, color: INK }}/>
        {bars.map((k, i) => (
          <Bar key={k} dataKey={k} name={k} fill={pickColor(k, colors, i)} radius={[2, 2, 0, 0]}/>
        ))}
        {areas.map((k, i) => (
          <Area key={k} dataKey={k} name={k} stroke={pickColor(k, colors, bars.length + i)} fill={pickColor(k, colors, bars.length + i)} fillOpacity={0.18} strokeWidth={2}/>
        ))}
        {lines.map((k, i) => (
          <Line key={k} dataKey={k} name={k} stroke={pickColor(k, colors, bars.length + areas.length + i)} strokeWidth={2} dot={{ r: 3 }}/>
        ))}
      </ComposedChart>
    );
  }

  if (type === 'line') {
    return (
      <LineChart data={data}>
        <CartesianGrid stroke={RULE} strokeDasharray="3 3"/>
        <XAxis dataKey={xKey} {...axisProps}/>
        <YAxis {...axisProps}/>
        <Tooltip {...tooltipChrome}/>
        <Legend wrapperStyle={{ fontSize: 12, color: INK }}/>
        {seriesKeys.map((s, i) => (
          <Line key={s.key} dataKey={s.key} name={s.name} stroke={pickColor(s.key, colors, i)} strokeWidth={2} dot={{ r: 3 }}/>
        ))}
      </LineChart>
    );
  }

  if (type === 'area') {
    return (
      <AreaChart data={data}>
        <CartesianGrid stroke={RULE} strokeDasharray="3 3"/>
        <XAxis dataKey={xKey} {...axisProps}/>
        <YAxis {...axisProps}/>
        <Tooltip {...tooltipChrome}/>
        <Legend wrapperStyle={{ fontSize: 12, color: INK }}/>
        {seriesKeys.map((s, i) => (
          <Area key={s.key} dataKey={s.key} name={s.name} stackId={stacked ? 'a' : undefined} stroke={pickColor(s.key, colors, i)} fill={pickColor(s.key, colors, i)} fillOpacity={0.25}/>
        ))}
      </AreaChart>
    );
  }

  // Default + 'bar' + anything else → bar chart.
  return (
    <BarChart data={data}>
      <CartesianGrid stroke={RULE} strokeDasharray="3 3"/>
      <XAxis dataKey={xKey} {...axisProps}/>
      <YAxis {...axisProps}/>
      <Tooltip {...tooltipChrome}/>
      <Legend wrapperStyle={{ fontSize: 12, color: INK }}/>
      {seriesKeys.map((s, i) => (
        <Bar key={s.key} dataKey={s.key} name={s.name} stackId={stacked ? 'a' : undefined} fill={pickColor(s.key, colors, i)} radius={[2, 2, 0, 0]}/>
      ))}
    </BarChart>
  );
}

/* ─── Sparkline ─────────────────────────────────────────────────────
 * Tiny inline chart: line + filled mini-area + hover dot. No axes,
 * no legend, no big chrome — but a hover ring on the nearest point
 * with a small floating value pill (so it feels like MUI's sparkline,
 * not like a static decoration).
 */
function Sparkline({ spec }) {
  const data = Array.isArray(spec.data) ? spec.data : [];
  const xKey  = spec.xKey || 'name';
  const yKey  = spec.valueKey || (data[0] ? Object.keys(data[0]).find(k => k !== xKey) : null) || 'value';
  const width = Math.max(60, Math.min(360, Number(spec.width) || 120));
  const height= Math.max(20, Math.min(120, Number(spec.height) || 32));
  const color = (spec.colors && spec.colors[yKey]) || PALETTE[0];
  const showArea = spec.area !== false;
  const [hover, setHover] = React.useState(/** @type {number|null} */ (null));
  if (!data.length) return null;
  const vals = data.map(r => Number(r?.[yKey]));
  let min = Infinity, max = -Infinity;
  for (const v of vals) { if (isFinite(v)) { if (v < min) min = v; if (v > max) max = v; } }
  if (!isFinite(min) || !isFinite(max)) return null;
  if (min === max) { min -= 1; max += 1; }
  const pad = 2;
  const innerW = width  - pad * 2;
  const innerH = height - pad * 2;
  const xFor = i => pad + (data.length === 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);
  const yFor = v => pad + innerH - ((v - min) / (max - min)) * innerH;
  const pts = vals.map((v, i) => `${xFor(i)},${yFor(v)}`).join(' ');
  const areaPts = vals.length
    ? `${xFor(0)},${pad + innerH} ${pts} ${xFor(vals.length - 1)},${pad + innerH}`
    : '';
  const handleMove = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const idx = Math.max(0, Math.min(data.length - 1, Math.round(((x - pad) / innerW) * (data.length - 1))));
    setHover(idx);
  };
  const hovered = hover != null ? data[hover] : null;
  return (
    <span style={{display:'inline-block',position:'relative',verticalAlign:'middle',width,height,margin:'0 4px'}}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} onMouseMove={handleMove} onMouseLeave={() => setHover(null)} style={{display:'block',cursor:'crosshair'}}>
        {showArea && <polygon points={areaPts} fill={color} fillOpacity={0.18}/>}
        <polyline points={pts} stroke={color} strokeWidth={1.5} fill="none"/>
        {hover != null && (
          <circle cx={xFor(hover)} cy={yFor(vals[hover])} r={3} fill="#fff" stroke={color} strokeWidth={1.5}/>
        )}
      </svg>
      {hovered && (
        <span style={{
          position:'absolute', top:-22, left: xFor(hover) - 20,
          background:'#fff', border:`1px solid ${RULE}`, borderRadius:6,
          padding:'2px 6px', fontSize:11, fontWeight:600, color:INK,
          boxShadow:'0 2px 8px rgba(0,0,0,0.08)', whiteSpace:'nowrap', pointerEvents:'none',
        }}>
          {String(hovered[xKey] ?? '')}: {String(hovered[yKey] ?? '')}
        </span>
      )}
    </span>
  );
}

/* ─── Heatmap ───────────────────────────────────────────────────────
 * Custom SVG implementation with axis labels, in-cell values, and a
 * React-state hover tooltip — MUI X heatmap parity (it's premium
 * there anyway). Spec: { xLabels, yLabels, values[y][x] }. Colour
 * ramps from neutral `#FCF8F7` up to the focal orange `#FF6428`.
 */
function HeatmapBody({ spec }) {
  const xLabels = Array.isArray(spec.xLabels) ? spec.xLabels : [];
  const yLabels = Array.isArray(spec.yLabels) ? spec.yLabels : [];
  const values  = Array.isArray(spec.values)  ? spec.values  : [];
  const showValues = spec.showValues !== false;
  const [hover, setHover] = React.useState(/** @type {{x:number,y:number}|null} */ (null));
  if (!xLabels.length || !yLabels.length || !values.length) {
    return <ErrInline msg="Heatmap needs xLabels[], yLabels[], values[][]."/>;
  }
  let min = Infinity, max = -Infinity;
  for (const row of values) for (const v of row) {
    if (typeof v !== 'number' || !isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (!isFinite(min) || !isFinite(max)) return <ErrInline msg="Heatmap values must be numeric."/>;
  const range = max - min || 1;
  // Layout: left gutter for y-labels, bottom gutter for x-labels.
  const W = 100, H = 100;
  const padL = 14, padB = 10, padT = 2, padR = 2;
  const cellW = (W - padL - padR) / xLabels.length;
  const cellH = (H - padT - padB) / yLabels.length;
  const lerp = (a, b, t) => Math.round(a + (b - a) * t);
  const colorFor = v => {
    const t = Math.max(0, Math.min(1, (v - min) / range));
    return `rgb(${lerp(0xFC, 0xFF, t)},${lerp(0xF8, 0x64, t)},${lerp(0xF7, 0x28, t)})`;
  };
  const fmtNum = v => (typeof v === 'number' && isFinite(v))
    ? (Math.abs(v) >= 100 ? Math.round(v) : Number(v.toFixed(1)))
    : '—';
  const hoveredVal = hover ? values[hover.y]?.[hover.x] : null;
  return (
    <div style={{ position:'relative', width:'100%', height:'100%' }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height="100%" style={{ display:'block' }}>
        {values.map((row, y) => row.map((v, x) => {
          const cx = padL + x * cellW;
          const cy = padT + y * cellH;
          const isHover = hover && hover.x === x && hover.y === y;
          return (
            <g key={`${x}-${y}`} onMouseEnter={() => setHover({ x, y })} onMouseLeave={() => setHover(null)}>
              <rect
                x={cx} y={cy} width={cellW} height={cellH}
                fill={typeof v === 'number' && isFinite(v) ? colorFor(v) : '#F5EDE5'}
                stroke={isHover ? INK : '#fff'} strokeWidth={isHover ? 0.5 : 0.2}
              />
              {showValues && cellW > 6 && cellH > 6 && (
                <text
                  x={cx + cellW / 2} y={cy + cellH / 2}
                  textAnchor="middle" dominantBaseline="central"
                  style={{ fontSize: Math.min(cellW * 0.35, cellH * 0.4, 3.2), fill: contrastInk(typeof v === 'number' ? (v - min) / range : 0), pointerEvents:'none' }}
                >
                  {fmtNum(v)}
                </text>
              )}
            </g>
          );
        }))}
        {/* x-axis labels */}
        {xLabels.map((label, i) => (
          <text
            key={`x-${i}`} x={padL + i * cellW + cellW / 2} y={H - 1.5}
            textAnchor="middle" style={{ fontSize: 3, fill: MUTED }}
          >{String(label)}</text>
        ))}
        {/* y-axis labels */}
        {yLabels.map((label, i) => (
          <text
            key={`y-${i}`} x={padL - 1.5} y={padT + i * cellH + cellH / 2}
            textAnchor="end" dominantBaseline="central" style={{ fontSize: 3, fill: MUTED }}
          >{String(label)}</text>
        ))}
      </svg>
      {hover && (
        <div style={{
          position:'absolute', left:'50%', top:0, transform:'translate(-50%, -110%)',
          background:'#fff', border:`1px solid ${RULE}`, borderRadius:6,
          padding:'6px 10px', fontSize:12, color:INK,
          boxShadow:'0 4px 16px rgba(0,0,0,0.06)', pointerEvents:'none', whiteSpace:'nowrap',
        }}>
          <div style={{ fontWeight: 600 }}>{String(yLabels[hover.y])} · {String(xLabels[hover.x])}</div>
          <div style={{ color: MUTED, fontSize: 11 }}>{String(hoveredVal ?? '—')}</div>
        </div>
      )}
    </div>
  );
}

function contrastInk(t) {
  // Below ~0.55 the cell is light enough for dark ink; above, switch to white.
  return t > 0.55 ? '#fff' : INK;
}

/* ─── Candlestick ───────────────────────────────────────────────────
 * Custom SVG with axis labels + React-state hover tooltip showing
 * full OHLC. Up bars (close ≥ open) in Eraneos green; down bars in
 * red. Spec: { data: [{ name, open, high, low, close }, …] }.
 */
function CandlestickBody({ spec }) {
  const data = Array.isArray(spec.data) ? spec.data : [];
  const [hover, setHover] = React.useState(/** @type {number|null} */ (null));
  if (!data.length) return <ErrInline msg="Candlestick needs OHLC rows."/>;
  let min = Infinity, max = -Infinity;
  for (const r of data) {
    if (typeof r.low  === 'number' && r.low  < min) min = r.low;
    if (typeof r.high === 'number' && r.high > max) max = r.high;
  }
  if (!isFinite(min) || !isFinite(max)) return <ErrInline msg="Candlestick OHLC values must be numeric."/>;
  const pad = (max - min) * 0.08 || 1;
  const yMin = min - pad, yMax = max + pad;
  const yRange = yMax - yMin || 1;
  const W = 100, H = 100;
  const padL = 8, padB = 8, padT = 2, padR = 2;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const colW = innerW / data.length;
  const bodyW = colW * 0.55;
  const yFor = v => padT + innerH - ((v - yMin) / yRange) * innerH;
  const xFor = i => padL + i * colW + colW / 2;
  const fmt = n => (typeof n === 'number' ? n.toFixed(2) : '—');
  // Y-axis ticks: 4 evenly spaced labels.
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(t => yMin + t * yRange);
  return (
    <div style={{ position:'relative', width:'100%', height:'100%' }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height="100%" style={{ display:'block' }}>
        {ticks.map((t, i) => (
          <g key={`tick-${i}`}>
            <line x1={padL} x2={W - padR} y1={yFor(t)} y2={yFor(t)} stroke={RULE} strokeWidth={0.15}/>
            <text x={padL - 1} y={yFor(t)} textAnchor="end" dominantBaseline="central" style={{ fontSize: 2.5, fill: MUTED }}>{fmt(t)}</text>
          </g>
        ))}
        {data.map((row, i) => {
          const x = xFor(i);
          const up = (row.close ?? 0) >= (row.open ?? 0);
          const colour = up ? UP_GREEN : DOWN_RED;
          const yHigh  = yFor(row.high);
          const yLow   = yFor(row.low);
          const yOpen  = yFor(row.open);
          const yClose = yFor(row.close);
          const bodyTop = Math.min(yOpen, yClose);
          const bodyBot = Math.max(yOpen, yClose);
          const isHover = hover === i;
          return (
            <g key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              {/* invisible wide hit-area so the whole column catches hover */}
              <rect x={padL + i * colW} y={padT} width={colW} height={innerH} fill="transparent"/>
              <line x1={x} x2={x} y1={yHigh} y2={yLow} stroke={colour} strokeWidth={isHover ? 0.6 : 0.4}/>
              <rect
                x={x - bodyW / 2} y={bodyTop}
                width={bodyW} height={Math.max(0.3, bodyBot - bodyTop)}
                fill={colour} stroke={isHover ? INK : colour} strokeWidth={isHover ? 0.3 : 0.2}
              />
            </g>
          );
        })}
        {/* x-axis labels — every nth label if there are many */}
        {data.map((row, i) => {
          const stride = Math.max(1, Math.floor(data.length / 8));
          if (i % stride !== 0) return null;
          return (
            <text key={`xl-${i}`} x={xFor(i)} y={H - 1.5} textAnchor="middle" style={{ fontSize: 2.5, fill: MUTED }}>
              {String(row.name ?? '')}
            </text>
          );
        })}
      </svg>
      {hover != null && (
        <div style={{
          position:'absolute', left:'50%', top:0, transform:'translate(-50%, -110%)',
          background:'#fff', border:`1px solid ${RULE}`, borderRadius:6,
          padding:'6px 10px', fontSize:12, color:INK,
          boxShadow:'0 4px 16px rgba(0,0,0,0.06)', pointerEvents:'none', whiteSpace:'nowrap',
        }}>
          <div style={{ fontWeight: 600 }}>{String(data[hover].name ?? '')}</div>
          <div style={{ display:'grid', gridTemplateColumns:'auto auto', columnGap:8, rowGap:1, fontSize:11, marginTop:2 }}>
            <span style={{ color: MUTED }}>Open</span><span>{fmt(data[hover].open)}</span>
            <span style={{ color: MUTED }}>High</span><span>{fmt(data[hover].high)}</span>
            <span style={{ color: MUTED }}>Low</span><span>{fmt(data[hover].low)}</span>
            <span style={{ color: MUTED }}>Close</span><span>{fmt(data[hover].close)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ErrChart({ msg }) {
  return (
    <div style={{margin:'8px 0',padding:'10px 12px',background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:12}}>
      Interactive chart failed: {msg}
    </div>
  );
}

function ErrInline({ msg }) {
  return (
    <div style={{padding:'10px 12px',background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:12}}>
      {msg}
    </div>
  );
}
